/**
 * Care Team Type Definitions
 * BP-06-03: Care Team & Goals Frontend
 */

// ============================================================================
// Enums
// ============================================================================

export type CareTeamMemberCategory = 
  | 'medical'
  | 'therapy'
  | 'education'
  | 'legal'
  | 'other';

// ============================================================================
// Core Types
// ============================================================================

export interface CareTeamMemberType {
  id: string;
  slug: string;
  name: string;
  category: CareTeamMemberCategory;
  displayOrder: number;
}

export interface CareTeamMember {
  id: string;
  familyId: string;
  childId: string | null;
  memberTypeId: string;
  memberType?: CareTeamMemberType;
  name: string;
  title: string | null;
  organization: string | null;
  email: string | null;
  phone: string | null;
  fax: string | null;
  address: string | null;
  userId: string | null;
  specialty: string | null;
  npiNumber: string | null;
  licenseNumber: string | null;
  notes: string | null;
  isPrimary: boolean;
  isActive: boolean;
  startDate: string | null;
  endDate: string | null;
  rating: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface CareTeamMemberSummary {
  id: string;
  name: string;
  title: string | null;
  organization: string | null;
  memberType: CareTeamMemberType;
  isPrimary: boolean;
  isActive: boolean;
  phone: string | null;
  email: string | null;
}

export interface CareTeamByCategory {
  category: CareTeamMemberCategory;
  categoryLabel: string;
  members: CareTeamMemberSummary[];
}

// ============================================================================
// Provider Directory
// ============================================================================

export interface ProviderDirectoryResult {
  id: string;
  name: string;
  title: string | null;
  organization: string;
  specialty: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  npiNumber: string | null;
  memberTypeId: string;
  memberType: CareTeamMemberType;
  rating: number | null;
  reviewCount: number;
  acceptsInsurance: string[];
  distance: number | null; // miles
}

// ============================================================================
// Request/Response Types
// ============================================================================

export interface CreateCareTeamMemberRequest {
  memberTypeId: string;
  childId?: string | null;
  name: string;
  title?: string | null;
  organization?: string | null;
  email?: string | null;
  phone?: string | null;
  fax?: string | null;
  address?: string | null;
  specialty?: string | null;
  npiNumber?: string | null;
  licenseNumber?: string | null;
  notes?: string | null;
  isPrimary?: boolean;
  startDate?: string | null;
}

export interface UpdateCareTeamMemberRequest {
  memberTypeId?: string;
  childId?: string | null;
  name?: string;
  title?: string | null;
  organization?: string | null;
  email?: string | null;
  phone?: string | null;
  fax?: string | null;
  address?: string | null;
  specialty?: string | null;
  npiNumber?: string | null;
  licenseNumber?: string | null;
  notes?: string | null;
  isPrimary?: boolean;
  isActive?: boolean;
  startDate?: string | null;
  endDate?: string | null;
  rating?: number | null;
}

export interface CareTeamListResponse {
  items: CareTeamMember[];
  total: number;
}

export interface ProviderSearchParams {
  query?: string;
  category?: CareTeamMemberCategory;
  memberTypeId?: string;
  zipCode?: string;
  radius?: number;
  acceptsInsurance?: string;
  page?: number;
  limit?: number;
}

export interface ProviderSearchResponse {
  items: ProviderDirectoryResult[];
  total: number;
  page: number;
  limit: number;
}

// ============================================================================
// Filter Types
// ============================================================================

export interface CareTeamFilters {
  category?: CareTeamMemberCategory;
  memberTypeId?: string;
  childId?: string | null;
  isActive?: boolean;
  isPrimary?: boolean;
  search?: string;
}

// ============================================================================
// Export Types
// ============================================================================

export interface CareTeamExportOptions {
  format: 'csv' | 'pdf';
  includeInactive?: boolean;
  childId?: string | null;
}

// ============================================================================
// Form Types
// ============================================================================

export interface CareTeamMemberFormData {
  memberTypeId: string;
  childId: string | null;
  name: string;
  title: string;
  organization: string;
  email: string;
  phone: string;
  fax: string;
  address: string;
  specialty: string;
  npiNumber: string;
  licenseNumber: string;
  notes: string;
  isPrimary: boolean;
  startDate: string;
}

// ============================================================================
// UI State Types
// ============================================================================

export interface CareTeamUIState {
  viewMode: 'grid' | 'list';
  filters: CareTeamFilters;
  selectedMemberId: string | null;
  isFormOpen: boolean;
  editingMemberId: string | null;
}

// Care Team Category Labels
export const CARE_TEAM_CATEGORY_LABELS: Record<string, string> = {
  medical: 'Medical',
  therapy: 'Therapy',
  education: 'Education',
  support: 'Support',
  specialist: 'Specialist',
  other: 'Other',
};

export type ProviderType = 
  | 'physician'
  | 'specialist'
  | 'therapist'
  | 'nurse'
  | 'aide'
  | 'social_worker'
  | 'teacher'
  | 'attorney'
  | 'other';

export type ContactMethod = 'phone' | 'email' | 'portal' | 'fax';

export const providerTypeLabels: Record<ProviderType, string> = {
  physician: 'Physician',
  specialist: 'Specialist',
  therapist: 'Therapist',
  nurse: 'Nurse',
  aide: 'Aide',
  social_worker: 'Social Worker',
  teacher: 'Teacher',
  attorney: 'Attorney',
  other: 'Other',
};

export const providerCategoryLabels: Record<CareTeamMemberCategory, string> = {
  medical: 'Medical',
  therapy: 'Therapy',
  education: 'Education',
  legal: 'Legal',
  support: 'Support',
};

export const contactMethodLabels: Record<ContactMethod, string> = {
  phone: 'Phone',
  email: 'Email',
  portal: 'Portal',
  fax: 'Fax',
};

export function getCategoryForProviderType(providerType: ProviderType): CareTeamMemberCategory {
  const categoryMap: Record<ProviderType, CareTeamMemberCategory> = {
    physician: 'medical',
    specialist: 'medical',
    therapist: 'therapy',
    nurse: 'medical',
    aide: 'support',
    social_worker: 'support',
    teacher: 'education',
    attorney: 'legal',
    other: 'support',
  };
  return categoryMap[providerType] || 'support';
}

export function getProviderTypesByCategory(category: CareTeamMemberCategory): ProviderType[] {
  const typesByCategory: Record<CareTeamMemberCategory, ProviderType[]> = {
    medical: ['physician', 'specialist', 'nurse'],
    therapy: ['therapist'],
    education: ['teacher'],
    legal: ['attorney'],
    support: ['aide', 'social_worker', 'other'],
  };
  return typesByCategory[category] || [];
}

import { z } from 'zod';

export const createCareTeamMemberSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  providerType: z.string(),
  category: z.string(),
  specialty: z.string().optional(),
  organization: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().email().optional().or(z.literal('')),
  address: z.string().optional(),
  notes: z.string().optional(),
  isPrimary: z.boolean().optional(),
  isActive: z.boolean().optional(),
});

export const updateCareTeamMemberSchema = createCareTeamMemberSchema.partial();

export type CreateCareTeamMember = z.infer<typeof createCareTeamMemberSchema>;
export type UpdateCareTeamMember = z.infer<typeof updateCareTeamMemberSchema>;

export const ProviderType = {
  PHYSICIAN: 'physician',
  SPECIALIST: 'specialist',
  THERAPIST: 'therapist',
  NURSE: 'nurse',
  AIDE: 'aide',
  SOCIAL_WORKER: 'social_worker',
  TEACHER: 'teacher',
  ATTORNEY: 'attorney',
  OTHER: 'other',
} as const;

export const ProviderCategory = {
  MEDICAL: 'medical',
  THERAPY: 'therapy',
  EDUCATION: 'education',
  LEGAL: 'legal',
  SUPPORT: 'support',
} as const;

export const ContactMethod = {
  PHONE: 'phone',
  EMAIL: 'email',
  PORTAL: 'portal',
  FAX: 'fax',
} as const;
